/* eslint-disable i18next/no-literal-string */
import React from 'react';
import { RouteProps } from 'react-router';
import {
  isAlertsPageEnabled,
  isAppLauncherPageEnabled,
  isCreditMonitoringUIEnabled,
  isDeviceAlertWebViewEnabled,
  isDeviceDetailsPageEnabled,
  isDimeEnabled,
  isITPSupported,
  isNotificationSettingEnabled,
  isOneTimeScanEnabled,
  isSandboxEnabled,
} from 'common/utilities/FlightSetting';
import { pageDataService } from 'common/utilities/PageDataService';
import { ICreditAlertDialogProps } from 'features/creditMonitoring/CreditAlertDialog/CreditAlertDialog';
import { CreditReportDetailsTypes } from 'features/creditMonitoring/CreditReport/report/WebViewCreditReportDetailsDialog';
import {
  IRecommendationDialogProps,
  RecommendationDialogMode,
} from 'features/identity/breachRecommendation/RecommendationDialog.types';
import IdentityProxyPage from 'features/identity/IdentityProxyPage';
import objectHash from 'object-hash';
import { currentUserCsidQueryParamName, FeatureParam, openDialogSourceQueryParamName } from 'RouteConstants';
import { MonitoringDataType, MonitoringDataValue } from 'store';
import { BreachEntityGroupType } from 'store/identityMonitoring/breach.types';

// Route constants
export const dashboardPath = '/';
export const devicesListPath = '/devices';
export const alertsPath = '/alerts';
export const identityOnboardingPath = '/identity/onboarding';
export const creditMonitoringOnboardingPath = '/identity/credit/onboarding';
export const identityBasePath = '/identity';
export const loginMfaPath = '/loginmfa';
export const creditMonitoringBasePath = '/identity/credit';
export const creditMonitoringReportPath = '/identity/credit/report';
export const creditMonitoringReportDetailsDialogPath = '/identity/credit/report/details';
export const identityConsentPath = '/identity/consent';
export const identityManageMemberPath = '/identity/manageMember';
const itmMaintenancePagePath = '/identity/maintenance';
export const oneTimeScanPath = '/onetimescan';

// Device alert WebView Routes
const deviceAlertBasePath = '/alerts/device';
const secureConnectionBasePath = '/alerts/secureconnection';
const deviceWebViewAlertDetailsPath = `${deviceAlertBasePath}/:alertId`;
const secureConnectionWebViewAlertDetailsPath = `${secureConnectionBasePath}/:alertId`;

// Device WebView Routes
const addDevicePath = `/devices/addDevice`;
const otherDevicesPath = 'devices/otherdevices';

// Empty WebView page that can be used to preload a webview page
const emptyWebViewPath = `/webview`;

// ITP WebView Routes
const identityAlertBasePath = '/alerts/identity';
const itpWebViewAddBasePath = `${identityBasePath}/add`;
const itpWebViewAddPath = `${itpWebViewAddBasePath}/:dataType`;
const itpWebViewBulkAddPath = `${identityBasePath}/bulkAdd`;
const itpWebViewRemoveBasePath = `${identityBasePath}/remove`;
const itpWebViewRemovePath = `${itpWebViewRemoveBasePath}/:dataType`;
const itpWebViewVerifyBasePath = `${identityBasePath}/verify`;
const itpWebViewVerifyPath = `${itpWebViewVerifyBasePath}/:dataType`;
const itpWebViewUndoDialogBasePath = `${identityBasePath}/undo`;
const itpWebViewUndoDialogPath = `${itpWebViewUndoDialogBasePath}/:breachId`;
const itpWebViewSubscriptionRemovePath = `${identityBasePath}/removeSubscription`;

// Upsell WebView route
const upsellWebViewPath = `/upsell`;

// side panels webview routes
export const notificationSettingsRoute = '/notificationSettings';
export const globalCopilotWebviewPath = '/copilot';
export const privacyPanelRoute = '/privacy';
export const familySharingPanelRoute = '/familysharing';

// Back-compat route
const itpWebViewBreachDetailsPath = `${identityAlertBasePath}/:breachId`;

// ITP WebView Url builders
export const requiresDialogClosingQueryParamName = 'requiresDialogClosingEvent';

// These routes are added just to stay in sync with the routes supported by apps.
// Although these pages are not supported on browser, we still need to register them in case
// user clicks on "launch in browser" from app launcher page.
const appOnlyPaths: Record<string, string> = {
  privacyProtection: '/privacyprotection',
  trustedNetworks: '/privacyprotection/trustednetworks',
  saferWifiPath: '/privacyprotection/saferwifi',
};

// Routes for side panels
export const sidePanelPaths: Record<string, string | undefined> = {
  privacyPanel: privacyPanelRoute,
  familySharingPanel: familySharingPanelRoute,
  globalCopilot: pageDataService.isCopilotEnabled() ? globalCopilotWebviewPath : undefined,
  notificationSettings: isNotificationSettingEnabled() ? notificationSettingsRoute : undefined,
};

export const buildItmMaintenancePath = (feature: FeatureParam, showBackButton = false) =>
  `${itmMaintenancePagePath}?feature=${feature}&showBackButton=${showBackButton}`;

export const buildConsentRequestPath = (requestedCsid: string | undefined) =>
  requestedCsid ? `${identityConsentPath}?${currentUserCsidQueryParamName}=${requestedCsid}` : identityConsentPath;

export const buildIdentityAlertPath = (
  breachId: string,
  dialogSource: IRecommendationDialogProps['dialogSource'],
  csid = '',
  mode: RecommendationDialogMode | '' = ''
) => `${identityBasePath}/?breachId=${breachId}&mode=${mode}&csid=${csid}&dialogSource=${dialogSource}`;

export const buildCMAlertPath = (artifactId: string, csid: string, dialogSource: ICreditAlertDialogProps['dialogSource']) =>
  `${creditMonitoringBasePath}/?artifactId=${artifactId}&csid=${csid}&dialogSource=${dialogSource}`;

export const buildIdentityFamilyMemberPath = (csid?: string) => `${identityBasePath}${csid ? `?csid=${csid}` : ''}`;

export const buildCreditMonitoringFamilyMemberPath = (csid?: string) => `${creditMonitoringBasePath}${csid ? `?csid=${csid}` : ''}`;

export const buildIdentityOnboardingPath = (email?: string, csid?: string, isCmOnboardingFlow = false) => {
  const urlParams = new URLSearchParams();
  if (email) {
    urlParams.set('hash', objectHash({ email }));
  }
  if (csid) {
    urlParams.set('csid', csid);
  }
  if (pageDataService.isWebView()) {
    // Set query parameter to distinguish between in-webview and in-app (native dashboard) onboarding flows
    urlParams.set(openDialogSourceQueryParamName, 'webview');
  }

  return `${isCmOnboardingFlow ? creditMonitoringOnboardingPath : identityOnboardingPath}?${urlParams.toString()}`;
};

export const buildIdentityAddPath = (dataType: MonitoringDataType, csid?: string) =>
  `${itpWebViewAddBasePath}/${dataType}?&csid=${csid || ''}`;

export const buildIdentityBulkAddPath = (csid?: string, dataType?: string | null) => {
  let url = `${itpWebViewBulkAddPath}?&${requiresDialogClosingQueryParamName}=true`;
  if (csid) {
    url = `${url}&csid=${csid}`;
  }
  if (dataType) {
    url = `${url}&dataType=${dataType}`;
  }
  return url;
};

export const buildIdentityRemovePath = (dataType: MonitoringDataType, index: number, dataValue: MonitoringDataValue, csid?: string) =>
  `${itpWebViewRemoveBasePath}/${dataType}?index=${index}&hash=${objectHash({ value: dataValue.value })}&csid=${csid || ''}`;

export const buildIdentityVerifyPath = (dataType: MonitoringDataType, index: number, csid?: string) =>
  `${itpWebViewVerifyBasePath}/${dataType}?index=${index}&csid=${csid || ''}`;

export const buildUndoDialogPath = (
  breachId: string,
  groupName: BreachEntityGroupType | undefined,
  entityIds: string[],
  csid?: string
) => `${itpWebViewUndoDialogBasePath}/${breachId}?&csid=${csid || ''}&groupName=${groupName}&entityIds=${entityIds.join(',')}`;

export const buildRemoveSubscriptionPath = (csid?: string, name?: string, useCMStrings = false) =>
  csid
    ? `${itpWebViewSubscriptionRemovePath}?csid=${csid}&name=${name || ''}&useCMStrings=${useCMStrings}`
    : `${itpWebViewSubscriptionRemovePath}?useCMStrings=${useCMStrings}`;

export const buildCreditReportPath = (csid?: string, sectionId?: string) =>
  `${creditMonitoringReportPath}?csid=${csid || ''}${sectionId ? `#${sectionId}` : ''}`;

export const buildCreditReportDetailsDialogPath = (type: CreditReportDetailsTypes, index: number, csid?: string) =>
  `${creditMonitoringReportDetailsDialogPath}?type=${type}&index=${index}&csid=${csid || ''}`;

const isWebView = pageDataService.isWebView();
const enableWebViewRoutes = isWebView || isAppLauncherPageEnabled();

// All routes registered to react router for GA UX
// Please ensure route changes are in sync with route registration in Portal service
export const routes: (RouteProps & { key: string })[] = [
  {
    key: 'dashboard',
    exact: true,
    path: '/',
    component: React.lazy(() => import('features/dashboard/DashboardPage')),
  },
  {
    key: 'devices',
    exact: true,
    path: '/devices',
    component: React.lazy(() => import('features/device/DevicesPage')),
  },
];

// Must be before '/devices/:deviceId?' path
if (isWebView) {
  routes.push({
    key: 'emptyWebView',
    exact: true,
    path: emptyWebViewPath,
    component: React.lazy(() => import('features/identity/webView/EmptyWebViewPage')),
  });
}

if (isDeviceDetailsPageEnabled()) {
  routes.push({
    key: 'device_protection_history',
    exact: true,
    path: '/devices/:deviceId?',
    component: React.lazy(() => import('features/device/DeviceProtectionHistoryPage')),
  });
}

if (isAlertsPageEnabled()) {
  routes.push({
    key: 'alerts',
    exact: true,
    path: alertsPath,
    component: React.lazy(() => import('features/alerts/AlertsPage/AlertsPage')),
  });
}

if (enableWebViewRoutes) {
  routes.push({
    key: 'addDevice',
    exact: true,
    path: addDevicePath,
    component: React.lazy(() => import('features/device/WebView/WebViewAddDeviceDialog')),
  });

  routes.push({
    key: 'otherDevices',
    exact: true,
    path: otherDevicesPath,
    component: React.lazy(() => import('features/device/DevicesPage')),
  });

  if (isDeviceAlertWebViewEnabled()) {
    routes.push({
      key: 'deviceAlertDetails',
      exact: true,
      path: deviceWebViewAlertDetailsPath,
      component: React.lazy(() => import('features/device/WebView/WebViewDeviceAlertDialog')),
    });
  }

  if (isDeviceAlertWebViewEnabled()) {
    routes.push({
      key: 'secureConnectionAlertDetails',
      exact: true,
      path: secureConnectionWebViewAlertDetailsPath,
      component: React.lazy(() => import('features/device/WebView/WebViewDeviceAlertDialog')),
    });
  }

  if (isDimeEnabled()) {
    routes.push({
      key: 'upsell',
      exact: true,
      path: upsellWebViewPath,
      component: React.lazy(() => import('Paywall/WebViewUpsell')),
    });
  }

  for (const routeKey in sidePanelPaths) {
    if (Object.hasOwn(sidePanelPaths, routeKey) && sidePanelPaths[routeKey]) {
      routes.push({
        key: routeKey,
        exact: true,
        path: sidePanelPaths[routeKey],
        component: React.lazy(() => import('common/components/Layout/OfficeShellPanel/WebView/WebViewOfficeShellPanel')),
      });
    }
  }

  for (const routeKey in appOnlyPaths) {
    if (Object.hasOwn(appOnlyPaths, routeKey) && appOnlyPaths[routeKey]) {
      routes.push({
        key: routeKey,
        exact: true,
        path: appOnlyPaths[routeKey],
        component: React.lazy(() => import('./AppOnlyPage')),
      });
    }
  }
}

routes.push({
  key: 'identityOnboarding',
  exact: true,
  path: identityOnboardingPath,
  component: React.lazy(() => import('features/identity/onboarding/IdentityOnboardingProxyPage')),
});

if (isOneTimeScanEnabled()) {
  routes.push({
    key: 'oneTimeScan',
    exact: true,
    path: oneTimeScanPath,
    component: React.lazy(() => import('features/identity/OneTimeScan/OneTimeScanPage')),
  });
}

if (isITPSupported()) {
  routes.push({
    key: 'itmMaintenance',
    exact: true,
    path: itmMaintenancePagePath,
    component: React.lazy(() => import('./common/components/Errors/MaintenancePage')),
  });

  routes.push({
    key: 'identityMonitoring',
    exact: true,
    path: identityBasePath,
    component: IdentityProxyPage,
  });

  routes.push({
    key: 'identityConsent',
    exact: true,
    path: identityConsentPath,
    component: React.lazy(() => import('features/identity/consent/IdentityConsentPage')),
  });

  if (isCreditMonitoringUIEnabled()) {
    routes.push({
      key: 'loginMfaPage',
      exact: true,
      path: loginMfaPath,
      component: React.lazy(() => import('features/creditMonitoring/LoginMfaPage')),
    });

    routes.push({
      key: 'creditMonitoring',
      exact: true,
      path: creditMonitoringBasePath,
      component: React.lazy(() => import('features/creditMonitoring/CreditMonitoringProxyPage')),
    });

    routes.push({
      key: 'creditMonitoringOnboarding',
      exact: true,
      path: creditMonitoringOnboardingPath,
      component: React.lazy(() => import('features/creditMonitoring/onboarding/CreditMonitoringOnboardingPage')),
    });

    routes.push({
      key: 'creditMonitoringReport',
      exact: true,
      path: creditMonitoringReportPath,
      component: React.lazy(() => import('features/creditMonitoring/CreditReport/CreditReportPage')),
    });

    if (enableWebViewRoutes) {
      routes.push({
        key: 'creditMonitoringReportDetailsDialog',
        exact: true,
        path: creditMonitoringReportDetailsDialogPath,
        component: React.lazy(() => import('features/creditMonitoring/CreditReport/report/WebViewCreditReportDetailsDialog')),
      });
    }
  }

  if (enableWebViewRoutes) {
    // Note: this route is obsolete
    // We are keeping this route for back-compat such that native alert page can still open old links
    // However once alert page is refreshed with new GetAlerts API and we have confirmed no more usages
    // based on telemetry, we should remove this route.
    routes.push({
      key: 'itpAlertDetails',
      exact: true,
      path: itpWebViewBreachDetailsPath,
      component: React.lazy(() => import('features/identity/webView/WebViewRecommendationDialog')),
    });

    routes.push({
      key: 'identityAddPage',
      exact: true,
      path: itpWebViewAddPath,
      component: React.lazy(() => import('features/identity/webView/WebViewAddIdentityDialog')),
    });

    routes.push({
      key: 'identityBulkAddPage',
      exact: true,
      path: itpWebViewBulkAddPath,
      component: React.lazy(() => import('features/identity/webView/WebViewBulkAddIdentityDialog')),
    });

    routes.push({
      key: 'identityRemovePage',
      exact: true,
      path: itpWebViewRemovePath,
      component: React.lazy(() => import('features/identity/webView/WebViewRemoveIdentityDialog')),
    });

    routes.push({
      key: 'subscriptionRemovePage',
      exact: true,
      path: itpWebViewSubscriptionRemovePath,
      component: React.lazy(() => import('features/identity/webView/WebViewRemoveSubscriptionDialog')),
    });

    routes.push({
      key: 'identityVerifyPage',
      exact: true,
      path: itpWebViewVerifyPath,
      component: React.lazy(() => import('features/identity/webView/WebViewVerifyIdentityDialog')),
    });

    routes.push({
      key: 'identityUndoDialog',
      exact: true,
      path: itpWebViewUndoDialogPath,
      component: React.lazy(() => import('features/identity/webView/WebViewUndoDialog')),
    });

    routes.push({
      key: 'identityManageMemberDialog',
      exact: true,
      path: identityManageMemberPath,
      component: React.lazy(() => import('features/identity/webView/WebViewManageMemberDialog')),
    });
  }
}

if ((process.env.ENVIRONMENT === 'dev' || process.env.ENVIRONMENT === 'ppe') && isSandboxEnabled()) {
  routes.push({
    key: 'sandbox_401error',
    exact: true,
    path: '/sandbox/401',
    component: React.lazy(() => import('./common/components/Errors/ErrorPage401')),
  });
  routes.push({
    key: 'sandbox_404error',
    exact: true,
    path: '/sandbox/404',
    component: React.lazy(() => import('./common/components/Errors/ErrorPage404')),
  });
  routes.push({
    key: 'sandbox_500error',
    exact: true,
    path: '/sandbox/500',
    component: React.lazy(() => import('./common/components/Errors/ErrorPage500')),
  });
  routes.push({
    key: 'sandbox',
    exact: true,
    path: '/sandbox/:sandboxPageId?',
    component: React.lazy(() => import('features/sandbox/SandboxPage')),
  });
}
